import MomentUtils from '@date-io/moment';
import * as Sentry from '@sentry/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { createBrowserHistory } from 'history';
import AgentLandingLayout from 'layouts/AgentLanding';
import DashboardLayout from 'layouts/Dashboard';
import HomeLayout from 'layouts/Home';
import SimpleCreateLayout from 'layouts/SimpleCreate';
import UnsubscribeLayout from 'layouts/Unsubscribe';
import log from 'loglevel';
import React from 'react';
import ReactDOM from 'react-dom';
import { Route, Router, Switch } from 'react-router-dom';
import { Toaster } from 'sonner';
import { SWRConfig } from 'swr';

import 'assets/scss/material-dashboard-pro-react.scss';

import { ThemeProvider } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';

import AuthDataProvider from 'components/AuthDataProvider/AuthDataProvider';
import MapInfoProvider from 'components/DataMap/MapInfoProvider';
import LoadingIndicatorProvider from 'components/LoadingIndicator/LoadingIndicatorProvider';
import ModalProvider from 'components/ModalProvider/ModalProvider';

import { apiClient } from 'utils/api';
import theme from 'utils/theme';

const hist = createBrowserHistory();

log.setDefaultLevel(process.env.REACT_APP_LOGGER_LEVEL);

// Only enabled in prod, for now
if (process.env.REACT_APP_SENTRY_IO_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_IO_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ // Replay integration
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.replayCanvasIntegration(), // Enable canvas recording with Replay
      // Send console.log, console.warn, and console.error calls as logs to Sentry
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],
    tracesSampleRate: 0.3, // Reduce later if we're getting too much data

    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    // Enable logs to be sent to Sentry
    enableLogs: true,
  });
}

const stripeOptions = {
  mode: 'setup',
  currency: 'usd',
  paymentMethodTypes: ['card'],
};

let stripePromise;

const isAgentLandingSubdomain = () => window.location.origin.match('://homeprice.');

const getStripePromise = () => {
  // Don't load stripe on agent landing page
  if (isAgentLandingSubdomain()) return null;

  if (!stripePromise) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY).catch(() => null);
  }

  return stripePromise;
};

const App = () => {
  const isAgentLandingPage = isAgentLandingSubdomain();
  const currentStripePromise = getStripePromise();

  let routes;

  if (isAgentLandingPage) {
    routes = (
      <Route path="/" component={AgentLandingLayout} />
    );
  } else {
    routes = (
      <Switch>
        <Route path="/dashboard" component={DashboardLayout} />
        <Route path="/launch" component={SimpleCreateLayout} />
        <Route path="/unsubscribe" component={UnsubscribeLayout} />
        <Route path="/" component={HomeLayout} />
      </Switch>
    );
  }

  const appContent = (
    <ModalProvider>
      <SWRConfig
        value={{
          revalidateOnFocus: false, // Can revisit this in the future
          fetcher: (url, params) => apiClient.get(url, { params }).then((res) => {
            log.debug('called', url, 'with params', { params }, 'and got', res.data);
            return res.data;
          }),
          // onError: (error, key) => {
          //   // TODO: Check for error, copy showAPIErrorAlert logic, maybe use React Hot Toast instead of our own alerts?
          // },
        }}
      >
        <AuthDataProvider>
          <LoadingIndicatorProvider>
            {routes}
          </LoadingIndicatorProvider>
        </AuthDataProvider>
      </SWRConfig>
    </ModalProvider>
  );

  return (
    <Router history={hist}>
      <SpeedInsights />

      <Toaster richColors closeButton duration={8000} />

      <ThemeProvider theme={theme}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <MapInfoProvider>
            {currentStripePromise ? (
              <Elements stripe={currentStripePromise} options={stripeOptions}>
                {appContent}
              </Elements>
            ) : appContent}
          </MapInfoProvider>
        </MuiPickersUtilsProvider>
      </ThemeProvider>
    </Router>
  );
};

ReactDOM.render(<App />, document.getElementById('root'));
